import { atom, useAtomValue, useStore } from 'jotai'
import { isEqual } from 'lodash-es'
import { useEffect, useMemo, useRef } from 'react'
import {
  debugTimeOverrideEnabledStorageAtom,
  debugTimeOverrideStorageAtom,
} from 'src/components/Admin/DebugWidget/TimeOverride/utils'
import { useServerTime } from 'src/services/restful/events'

const nowAtom = atom(Date.now())
const offsetAtom = atom(0)
let timer: number | undefined
let subscribers = 0

function subscribe(store: ReturnType<typeof useStore>) {
  subscribers++
  if (timer == null) {
    timer = window.setInterval(() => {
      store.set(nowAtom, Date.now())
    }, 500)
  }
}

function unsubscribe() {
  subscribers--
  if (subscribers === 0 && timer != null) {
    window.clearInterval(timer)
    timer = undefined
  }
}

/**
 * Hook to use the current timestamp in milliseconds
 *
 * It is recommended to use the factory overload if possible to reduce unnecessary re-renders.
 *
 * Overload #1
 */
export default function useNow(): number
/**
 * Hook to generate a value derived from the current timestamp
 *
 * Overload #2
 *
 * @param factory - The function to generate the derived value.
 *                  Note that the function will be called on every tick (~500ms)
 *                  and should be memoized (e.g. using `useCallback`).
 *                  The result will be auto-deduped.
 */
export default function useNow<T>(factory: (now: number) => T): T
/**
 * The implementation
 */
export default function useNow<T>(factory?: (now: number) => T) {
  const store = useStore()

  useEffect(() => {
    subscribe(store)
    return unsubscribe
  }, [store])

  const { data: serverTimeUTC } = useServerTime()

  useEffect(() => {
    if (serverTimeUTC) {
      store.set(offsetAtom, new Date(serverTimeUTC).getTime() - Date.now())
    }
  }, [serverTimeUTC, store])

  const lastResultRef = useRef<number | T>(null)
  const resultAtom = useMemo(() => {
    return atom(get => {
      // Read override (mock) time config
      const enabledOverride = get(debugTimeOverrideEnabledStorageAtom)
      const overrideTime =
        enabledOverride && get(debugTimeOverrideStorageAtom).valueOf()

      const time = overrideTime
        ? // Mock time
          overrideTime
        : // Real time with server time offset
          get(nowAtom) + get(offsetAtom)

      const result = factory ? factory(time) : time
      if (!isEqual(lastResultRef.current, result)) {
        lastResultRef.current = result
      }
      return lastResultRef.current
    })
  }, [factory])

  return useAtomValue(resultAtom)
}
